import React, {FC} from 'react';

import {useLocalize} from '../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Localizer} from '../../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {TeamModel} from '../../../../libs/shared/core/src/models/api/teamModel';
import {RuleModelStrict} from '../../../models/api/ruleModel';
import {SettingsSearchRuleModel} from '../../../models/api/settingsSearchModel';
import {buildRuleDescription} from '../../../settings/rules/computeRuleRecipeDescription';
import {RuleParametersDependencies} from '../../../settings/rules/ruleConditionModel';
import {computeIsRuleRecipeBundle} from '../../../settings/rules/ruleHelpers';
import {maybeRenderAccessory} from './settingsSearchAccessoryHelpers';
import {
  SettingsSearchDropdownContent,
  SettingsSearchDropdownItem,
  SettingsSearchDropdownItemLeftIcon,
  SettingsSearchDropdownSubtitle,
  SettingsSearchDropdownTitle,
} from './settingsSearchCommon';
import {SettingsSearchDropdownItemBaseProps} from './settingsSearchCommonTypes';
import {SettingsSearchRuleIcon} from './settingsSearchRuleIcon';

/*
 * Props.
 */

interface SettingsSearchDropdownRuleItemProps extends SettingsSearchDropdownItemBaseProps {
  ruleItem: SettingsSearchRuleModel['items'][0];
  team?: TeamModel;
  ruleParametersDependencies: RuleParametersDependencies;
}

/*
 * Component.
 */

export const SettingsSearchDropdownRuleItem: FC<SettingsSearchDropdownRuleItemProps> = (props) => {
  const {ruleItem} = props;
  const {rule} = ruleItem;

  const ruleName = computeIsRuleRecipeBundle(ruleItem.ruleBundleType) ? ruleItem.ruleBundleName : rule.name;
  const localize = useLocalize();
  const ruleDescription = computeRuleDescription(ruleItem, props.ruleParametersDependencies, localize);

  return (
    <SettingsSearchDropdownItem checkmarkLabel={ruleName} onSelect={props.onSelect}>
      <SettingsSearchDropdownItemLeftIcon>
        <SettingsSearchRuleIcon rule={rule} />
      </SettingsSearchDropdownItemLeftIcon>
      <SettingsSearchDropdownContent>
        <SettingsSearchDropdownTitle>
          {ruleName}
          {maybeRenderAccessory(rule, props.team)}
        </SettingsSearchDropdownTitle>
        <SettingsSearchDropdownSubtitle>{ruleDescription}</SettingsSearchDropdownSubtitle>
      </SettingsSearchDropdownContent>
    </SettingsSearchDropdownItem>
  );
};

/*
 * Helpers.
 */

function computeRuleDescription(
  ruleItem: SettingsSearchDropdownRuleItemProps['ruleItem'],
  ruleParametersDependencies: RuleParametersDependencies,
  localize: Localizer,
) {
  const {ruleBundleType, description: bundleDescription} = ruleItem;

  const ruleDescription =
    ruleBundleType &&
    buildRuleDescription(
      ruleBundleType,
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      ruleItem.ruleBundleRules as ReadonlyArray<RuleModelStrict>,
      ruleParametersDependencies,
      localize,
    );

  return ruleDescription || bundleDescription;
}
